import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Settings, Save, Lock, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/hooks';
import { supabase } from '../../../lib/supabase';
import { SettingsForm } from './components/SettingsForm';
import { useSubscription } from '../../../hooks/useSubscription';
import { useLoyaltySettings } from '../../../hooks/useLoyaltySettings';
import { DEFAULT_LOYALTY_SETTINGS, migrateLoyaltySettings, validateLoyaltySettings, ensureLoyaltySettings } from './utils/migration';
import toast from 'react-hot-toast';

function LoyaltySettings() {
  const { user, business } = useAuth();
  const { isFeatureAvailable } = useSubscription();
  const { isLoyaltyEnabled, updateLoyaltyEnabled } = useLoyaltySettings();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_LOYALTY_SETTINGS);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);

  const loyaltyEnabled = isFeatureAvailable('loyalty_program');

  useEffect(() => {
    loadSettings();
  }, [user?.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        setError('משתמש לא מחובר');
        setLoading(false);
        return;
      }

      let id = business?.id;

      if (!id) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('business_id')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;
        if (!userData?.business_id) throw new Error('לא נמצא עסק מקושר');

        id = userData.business_id;
      }

      setBusinessId(id);

      if (!loyaltyEnabled) {
        setLoading(false);
        setInitialLoad(false);
        return;
      }

      // Ensure settings exist and migrate if needed
      const migratedSettings = await ensureLoyaltySettings(supabase, id);
      setSettings(migratedSettings);
      setInitialLoad(false);

    } catch (error: any) {

      setError(error.message || 'שגיאה בטעינת ההגדרות');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (newSettings: any) => {
    if (!businessId || !loyaltyEnabled) return;

    // Validate before saving
    const validationError = validateLoyaltySettings(newSettings);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const { data: currentData, error: fetchError } = await supabase
        .from('businesses')
        .select('settings')
        .eq('id', businessId)
        .single();

      if (fetchError) throw fetchError;

      const updatedSettings = {
        ...currentData?.settings,
        loyalty: {
          enabled: newSettings.enabled,
          points: newSettings.points,
          diamonds: newSettings.diamonds,
          levels: Object.entries(newSettings.levels).reduce((acc, [level, data]: [string, any]) => ({
            ...acc,
            [level]: {
              diamonds_required: data.diamonds_required,
              benefits: {
                services_discount: data.benefits.services_discount,
                products_discount: data.benefits.products_discount,
                free_appointment_every: data.benefits.free_appointment_every,
                birthday_appointment: data.benefits.birthday_appointment
              }
            }
          }), {})
        }
      };

      const { error: updateError } = await supabase
        .from('businesses')
        .update({
          settings: updatedSettings
        })
        .eq('id', businessId);

      if (updateError) throw updateError;

      await updateLoyaltyEnabled(newSettings.enabled);

      setSettings(newSettings);
      toast.success('ההגדרות נשמרו בהצלחה');
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בשמירת ההגדרות');
    }
  };

  function levelLabel(level: string) {
    switch (level) {
      case 'silver': return 'כסף';
      case 'gold': return 'זהב';
      case 'diamond': return 'יהלום';
      case 'vip': return 'VIP';
      default: return level;
    }
  }

  if (!loyaltyEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-6 px-4">
        <div className="bg-gray-100 p-4 rounded-full">
          <Lock className="h-12 w-12 text-gray-400" />
        </div>
        <div className="text-center max-w-md">
          <h2 className="text-lg font-bold text-gray-900 mb-2">תכונה לא זמינה</h2>
          <p className="text-sm text-gray-600 mb-4">
            תכונת תוכנית הנאמנות אינה זמינה בחבילה הנוכחית שלך.
          </p>
          <Link
            to="/settings"
            className="inline-block px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg active:bg-blue-700 transition-colors"
          >
            חזרה להגדרות
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={() => loadSettings()}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg active:bg-blue-700 transition-colors"
        >
          נסה שוב
        </button>
      </div>
    );
  }

  // Don't show form until initial load is complete
  if (initialLoad) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">טוען הגדרות נאמנות...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/settings" className="text-gray-500 active:text-gray-700">
              <ArrowRight className="h-6 w-6" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">הגדרות נאמנות</h1>
            {/* System status indicator */}
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${settings.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {settings.enabled ? 'פעילה' : 'כבויה'}
            </div>
          </div>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const form = document.getElementById('loyalty-settings-form');
              if (form) {
                form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg active:bg-blue-700 transition-colors"
          >
            שמור
          </motion.button>
        </div>
      </div>

      <SettingsForm
        settings={settings}
        onSave={handleSave}
      />
    </div>
  );
}

export default LoyaltySettings;
